
import { Options, Vue } from 'vue-class-component';
import { useRoute } from 'vue-router';
import dayjs from 'dayjs';
import { eventBus } from '@/eventBus';

import Panorama from '@/components/UI/Panorama.vue';
import ShootingPinTable from '@/components/Shootings/ShootingPinTable.vue';
import ShootingPinPlan from '@/components/Shootings/ShootingPinPlan.vue';
import ShootingPinControls from '@/components/Shootings/ShootingPinControls.vue';

import { camelCaseKeys } from '@/utilities/helpers';
import { Panorama as PanoramaClass } from '@/classes/Panorama';
import { Point as PointClass } from '@/classes/Point';
import { PointCreator } from '@/classes/PointCreator';
import { PointMoveEvent } from '@/classes/widgets/PointMoveEvent';

import {
  getUploadShootingRequest,
  pinPanoramasToPointsRequest,
} from '@/requestManager/entities/UploadShootingRequests';
import { createPointRequest, movePointRequest } from '@/requestManager/entities/PointRequests';
import { ShootingPinPoint } from '@/classes/pages/shootingPin/ShootingPinPoint';

@Options({
  name: 'ShootingPin',
  components: {
    Panorama,
    ShootingPinTable,
    ShootingPinPlan,
    ShootingPinControls,
  },

  data: () => ({
    shooting: null,
    project: null,
    panoramas: null,
    activePanoramaIndex: null,
    plans: null,
    activePlanIndex: null,
    points: null,
    activePointId: null,
    route: null,
    progressBarState: {
      show: false,
      progress: 0,
    },
  }),
  computed: {
    shootingDate(): string {
      return dayjs(this.shooting.uploadDate, 'YYYY-MM-DD').format('DD.MM.YYYY');
    },

    activePanorama() {
      return this.activePanoramaIndex !== null
        ? this.panoramas[this.activePanoramaIndex]
        : null;
    },
    pointsForPlan() {
      if (!this.activePlan) {
        return [];
      }

      const filteredPoints = this.points.filter((point: PointClass) => point.planId === this.activePlan.id);

      const getShootingPinPointFromPoint = (point: PointClass) =>
        new ShootingPinPoint({
          ...point,
          dotted: Boolean(this.panoramas.find((panorama: PanoramaClass) => panorama.pointId === point.id)),
        });

      return filteredPoints.map(getShootingPinPointFromPoint);
    },

    activePlan() {
      return this.plans[this.activePlanIndex];
    },

    unpinFromActivePanoramaButtonIsEnabled(): boolean {
      return Boolean(this.activePanorama?.pointId);
    },

    activePointIsPinnable(): boolean {
      if (!this.activePointId) {
        return false;
      }

      const hasPinnedPanorama = Boolean(
        this.panoramas.find((panorama: PanoramaClass) =>
          panorama.pointId === this.activePointId
        ));

      return !hasPinnedPanorama && !this.activePanorama?.pointId;
    },
    activePointIsUnpinnable(): boolean {
      if (!this.activePointId) {
        return false;
      }

      return Boolean(this.panoramas.find((panorama: PanoramaClass) =>
        panorama.pointId === this.activePointId
      ));
    },

    hasPreviousPanorama(): boolean {
      if (!this.panoramas) {
        return false;
      }

      return this.activePanoramaIndex !== 0;
    },
    hasNextPanorama(): boolean {
      if (!this.panoramas) {
        return false;
      }

      return Boolean(this.panoramas[this.activePanoramaIndex + 1]);
    },
    progressBarStyle(): string {
      return `width: ${this.progressBarState.progress}%`;
    },
  },

  async created() {
    await this.getUploadShooting();
    this.setActivePanorama(0);
    this.setActivePlan(0);
  },

  methods: {
    async getUploadShooting(): Promise<any> {
      this.route = useRoute();
      const { data } = await getUploadShootingRequest(this.route.params.uploadShootingId);

      this.shooting = camelCaseKeys(data.shooting);
      this.project = camelCaseKeys(data.project);
      this.panoramas = data.panoramas.map((panorama: any) => new PanoramaClass(camelCaseKeys(panorama)));
      this.plans = data.plans.map((plan: any) => camelCaseKeys(plan));
      this.points = data.points.map((point: any) => camelCaseKeys(point));
    },
    setActivePanorama(index: number): void {
      this.activePanoramaIndex = index;
    },
    setActivePlan(index: number): void {
      this.activePlanIndex = index;
    },
    async submit(): Promise<any> {
      const pinPanoramasToPointsRequestData = this.panoramas.map(
        (panorama: { id: number; pointId: number; }) => ({
        panoramaId: panorama.id,
        pointId: panorama.pointId,
      }));

      await pinPanoramasToPointsRequest(+(this.route.params.uploadShootingId), pinPanoramasToPointsRequestData);
      window.location.href = `/admin/shooting-check?shooting_id=${this.route.params.uploadShootingId}`;
    },

    pinPanoramaToActivePoint(): void {
      this.panoramas[this.activePanoramaIndex].pointId = this.activePointId;

      // здесь нет ошибки несоответствия типов
      if (this.hasNextPanorama) {
        this.setNextPanoramaActive();
      }
    },
    unpinPanoramaFromActivePoint(): void {
      const activePointPanoramas = this.panoramas.filter((panorama: PanoramaClass) =>
        panorama.pointId === this.activePointId
      );

      for (let activePointPanorama of activePointPanoramas) {
        activePointPanorama.pointId = null;
      }
    },

    unpinPointFromActivePanorama(): void {
      this.panoramas[this.activePanoramaIndex].pointId = null;
    },

    setPreviousPanoramaActive(): void {
      this.activePanoramaIndex--;
    },
    setNextPanoramaActive(): void {
      this.activePanoramaIndex++;
    },
    onDrop(e: DragEvent): void {
      this.$refs.fileUploader.files = e.dataTransfer?.files;
      this.loadPanoramas();
      e.preventDefault();
    },

    validateFile(type: String): boolean {
      return type === 'image/png' || type === 'image/jpeg' || type === 'image/jpg';
    },

    // для теста прогресс-бара

    // uploadPanoram() {
    //   return new Promise(resolve => setTimeout(() => resolve(true), 1000));
    // },

    async loadPanoramas(): Promise<any> {
      const files = this.$refs.fileUploader.files;
      this.progressBarState.show = true;

      for (let i = 0; i < files.length; i++) {
        const isValid = this.validateFile(files[i].type);

        if (isValid) {
          // const response = await this.uploadPanoram(this.shooting.id, files[i]);
          // this.panoramas.push(response.data);
          this.progressBarState.progress += 100 / files.length;
        }
      }

      setTimeout(() => {
        this.progressBarState.progress = 0;
        this.progressBarState.show = false;
      }, 500);
    },

    async createPoint(): Promise<any> {
      const pointCreator = new PointCreator(0, 0, this.activePlan.id);

      const response = await createPointRequest(pointCreator);
      const newPoint = new PointClass({
        ...camelCaseKeys(response.data.point),
        draggable: true,
      });
      this.points.push(newPoint);

      eventBus.emit('shooting-pin-page.point', {
        action: 'CREATED',
        payload: newPoint,
      });
    },

    async pointMovedHandler(event: PointMoveEvent): Promise<any> {
      this.moveLocalPoint(event);

      await movePointRequest({
        id: event.id,
        params: event,
      });
    },
    moveLocalPoint(event: PointMoveEvent): void {
      const point = this.points.find((loopPoint: PointClass) => loopPoint.id === event.id);

      if (!point) {
        return;
      }

      point.x = event.x;
      point.y = event.y;
    },
  },
})
export default class ShootingCreate extends Vue {}
